import React, { useCallback } from 'react';

import { Paper, Portal } from '@mui/material';
import type { TElement } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import { composeRefs } from 'src/helpers/compose-refs';
import { makeStyles } from 'tss-react/mui';

import type { useTriggerPicker } from './useTriggerPicker';
import { getEditorPopoverStyles } from '../../AlineMarkdownEditor/utils/styles';

const useStyles = makeStyles()(theme => ({
  hiddenInput: {
    background: 'transparent',
    border: 'none',
    caretColor: theme.palette.text.primary,
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    left: 0,
    /**
     * The mirror inherits these from the editor but the browser stylesheet resets them on an
     * input, so they are re-inherited to keep the two measuring the same text the same way.
     */
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    margin: 0,
    minWidth: 0,
    outline: 'none',
    padding: 0,
    position: 'absolute',
    textTransform: 'inherit',
    top: 0,
    // Overflows the mirror by a pixel so the caret is drawable without widening the span.
    width: 'calc(100% + 1px)',
    wordSpacing: 'inherit',
  },
  /**
   * Sizes the search input to exactly the text it holds: the input is absolutely positioned
   * over a hidden mirror of its own value, so the span is exactly as wide as the text. A
   * character-count estimate would leave a visible gap before whatever follows the mention
   * input, which shows up whenever the picker is opened inside existing text.
   */
  inputSizer: {
    '&::after': {
      // The zero-width space keeps a full-height line box while the search text is empty.
      content: 'attr(data-value) "\\200b"',
      font: 'inherit',
      visibility: 'hidden',
      whiteSpace: 'pre',
    },
    display: 'inline-block',
    // The input is absolutely positioned, so the hidden mirror text alone sets the width.
    position: 'relative',
  },
  panel: {
    ...getEditorPopoverStyles(theme).popoverPaper,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxWidth: 'calc(100vw - 24px)',
    padding: theme.spacing(1),
    zIndex: theme.zIndex.modal - 1,
  },
  results: {
    '&&': { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', width: '100%' },
  },
}));

interface TriggerPickerInputProps {
  /** Accessible name for the hidden search input. */
  ariaLabel: string;
  /** Slate's own props for the trigger node. */
  elementProps: PlateElementProps<TElement>;
  /** The menu's contents, rendered inside the popper surface. */
  menu: React.ReactNode;
  menuClassName: string;
  picker: ReturnType<typeof useTriggerPicker>;
  /** The literal character that opened the picker, rendered before the input. */
  trigger: string;
  /**
   * A dialog this picker owns, rendered inside the trigger so it stays mounted while the
   * picker is suspended. It portals itself, so it adds no DOM inside the inline element.
   */
  dialog?: React.ReactNode;
  /** Extra props for the popper surface, such as an id or a test attribute. */
  menuProps?: Record<string, unknown>;
}

/**
 * The render shell every trigger picker shares: the inline trigger, its hidden search
 * input, and a portaled popper surface holding the menu.
 *
 * Returns the span directly rather than wrapping it in `PlateElement`. Wrapping would apply
 * Slate's `data-slate-*` attributes twice and add a DOM node inside the inline element,
 * which breaks Slate's DOM-to-model resolution.
 */
export const TriggerPickerInput = React.memo(function TriggerPickerInput({
  ariaLabel,
  dialog,
  elementProps,
  menu,
  menuClassName,
  menuProps,
  picker,
  trigger,
}: TriggerPickerInputProps): React.JSX.Element {
  const { classes } = useStyles();
  const { attributes, children } = elementProps;
  const { setQuery } = picker;
  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value),
    [setQuery]
  );

  return (
    <span {...attributes} ref={composeRefs<HTMLSpanElement>(picker.setAnchorEl, attributes.ref)}>
      <span contentEditable={false}>
        <span>{trigger}</span>
        <span className={classes.inputSizer} data-value={picker.query}>
          <input
            ref={picker.inputRef}
            aria-label={ariaLabel}
            className={classes.hiddenInput}
            type="text"
            value={picker.query}
            onChange={handleQueryChange}
            onKeyDown={picker.handleKeyDown}
          />
        </span>
      </span>
      {children}
      <Portal>
        <Paper
          ref={picker.setPopperElement}
          className={classes.panel}
          elevation={8}
          {...menuProps}
          {...picker.popperAttributes.popper}
          style={{
            ...picker.popperStyles.popper,
            height: picker.panelMaxHeight,
            maxHeight: picker.panelMaxHeight,
            width: picker.panelWidth,
          }}
        >
          <div className={`${menuClassName} ${classes.results}`}>{menu}</div>
        </Paper>
      </Portal>
      {dialog}
    </span>
  );
});
